/**
 * API client utility functions with app token support
 * 
 * The client uses app tokens (preferred) or Auth0 tokens (fallback for profile creation).
 * App tokens contain embedded permissions, eliminating per-request DB lookups on the server.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export interface ApiError {
  message: string
  status: number
  code?: string
}

type GetTokenFn = () => Promise<string | null>
type RefreshTokenFn = () => Promise<string | null>

class ApiClient {
  private baseUrl: string
  private getToken: GetTokenFn | null = null
  private refreshToken: RefreshTokenFn | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  /**
   * Set the token getter function (called from useAuthenticatedApi hook)
   * @param getToken - Function to get the current access token (app or Auth0)
   * @param refreshToken - Optional function to refresh the token on 401 errors
   */
  setTokenGetter(getToken: GetTokenFn, refreshToken?: RefreshTokenFn) {
    this.getToken = getToken
    this.refreshToken = refreshToken ?? null
  }

  /**
   * Clear the token getter (on logout)
   */
  clearTokenGetter() {
    this.getToken = null
    this.refreshToken = null
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireAuth: boolean = false,
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    // Add auth token if available
    if (this.getToken) {
      try {
        const token = await this.getToken()
        if (token) {
          ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
        } else if (requireAuth) {
          throw {
            message: 'Authentication required',
            status: 401,
          } as ApiError
        }
      } catch (error) {
        if (requireAuth) {
          throw {
            message: 'Failed to get authentication token',
            status: 401,
          } as ApiError
        }
      }
    } else if (requireAuth) {
      throw {
        message: 'Authentication not configured',
        status: 401,
      } as ApiError
    }

    const config: RequestInit = {
      headers,
      ...options,
    }

    try {
      const response = await fetch(url, config)

      // Handle 401 Unauthorized - try to refresh token and retry once
      if (response.status === 401 && !isRetry && this.refreshToken) {
        console.log('Got 401, attempting token refresh...')
        const newToken = await this.refreshToken()
        if (newToken) {
          // Retry the request with the new token
          return this.request<T>(endpoint, options, requireAuth, true)
        }
      }

      if (response.status === 401) {
        throw {
          message: 'Unauthorized - please log in again',
          status: 401,
        } as ApiError
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: response.statusText,
        }))
        throw {
          message: error.error || error.message || 'An error occurred',
          status: response.status,
          code: error.code,
        } as ApiError
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }

      return {} as T
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error) {
        throw error
      }
      throw {
        message: error instanceof Error ? error.message : 'Network error',
        status: 0,
      } as ApiError
    }
  }

  // Public endpoints (no auth required)
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' }, false)
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      false
    )
  }

  // Authenticated endpoints
  async authGet<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' }, true)
  }

  async authPost<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      true
    )
  }

  async authPut<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      },
      true
    )
  }

  async authDelete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' }, true)
  }

  async authPatch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        ...options,
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
      },
      true
    )
  }
}

export const apiClient = new ApiClient(API_BASE_URL)
